import { useCallback } from 'react'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'
import useSentry from 'Pods/Shared/Hooks/useSentry'
import I18n from 'Pods/Shared/I18n'

import { formatLongDate } from 'Utils/FormatDate'
import { NOOP } from 'Utils/stableDefaults'

import { PLAN_TYPE } from '../../constants'

const COMMIT_PATH = '/profile/subscription/plan_switch'

function useSwitchPlanModal({
  subscription, locale, onClose, previewData, onSwitchSuccess = NOOP,
}) {
  const currentPlan = subscription?.planType === PLAN_TYPE.MONTHLY
    ? PLAN_TYPE.MONTHLY
    : PLAN_TYPE.YEARLY
  const targetPlan = currentPlan === PLAN_TYPE.YEARLY
    ? PLAN_TYPE.MONTHLY
    : PLAN_TYPE.YEARLY

  const translate = useCallback(
    (key, options = {}) => I18n.t(
      `subscription_management.switch_plan_modal.${key}`,
      { ns: 'rails', lng: locale, ...options }
    ),
    [locale]
  )

  const { postData, isLoading: isCommitLoading } = useApiPost()
  const { showSuccess, showError } = useNotifications()
  const { captureException } = useSentry()

  const noticeMessage = targetPlan === PLAN_TYPE.MONTHLY
    ? translate('notice.yearly_to_monthly', { date: formatLongDate(previewData.effective_at, locale) })
    : translate('notice.monthly_to_yearly')

  const primaryButtonText = targetPlan === PLAN_TYPE.MONTHLY
    ? translate('submit.to_monthly')
    : translate('submit.to_yearly')

  const isSubmitDisabled = isCommitLoading

  const handleClose = useCallback(() => {
    if (isCommitLoading) return
    onClose()
  }, [onClose, isCommitLoading])

  const handleSubmit = useCallback(() => {
    postData({
      path: COMMIT_PATH,
      data: { to: targetPlan },
      onSuccess: (response) => {
        showSuccess(translate('toast.success'))
        onSwitchSuccess(response.data)
        onClose()
      },
      onError: (error) => {
        const message = error.response?.data?.error || translate('toast.error')
        showError(message)
        captureException(error, {
          tags: { context: 'switch_plan_commit' },
          extra: { subscriptionId: subscription?.id, target: targetPlan },
        })
      },
    })
  }, [
    postData,
    targetPlan,
    showSuccess,
    showError,
    captureException,
    onClose,
    onSwitchSuccess,
    subscription?.id,
    translate,
  ])

  return {
    currentPlan,
    targetPlan,
    isCommitLoading,
    noticeMessage,
    primaryButtonText,
    cancelButtonText: translate('cancel'),
    title: translate('title'),
    isSubmitDisabled,
    handleClose,
    handleSubmit,
  }
}

export default useSwitchPlanModal
